import React from 'react';
import {
  ChartBarIcon,
  InfoIcon,
  BadgeIcon,
  ClipboardListIcon,
  BellIcon,
  UsersIcon,
  CalendarIcon,
  PencilIcon,
  BuildingOfficeIcon,
  StarIcon,
  CloseIcon,
  LogoutIcon,
} from '../icons';
import { SidebarFedexporMark, PoweredByLytiks } from '../ui/Logo';

const Sidebar = ({ currentUser, onNav, onLogout, currentPage, isOpen = false, onClose = () => {}, adminUnreadCount = 0 }) => {
  const publicItems = [
    { name: 'Dashboard Público', page: 'public_dashboard', icon: <ChartBarIcon /> },
    { name: 'Metodología',       page: 'public_methodology', icon: <InfoIcon /> },
  ];
  const legalItems = [
    { name: 'Privacidad', page: 'legal_privacy', icon: <BadgeIcon /> },
    { name: 'Términos',   page: 'legal_terms',   icon: <InfoIcon /> },
  ];

  const sections = (() => {
    if (currentUser?.role === 'admin') return [
      { label: 'Administración', items: [
        { name: 'Gestión de Envíos',   page: 'admin_submissions', icon: <ClipboardListIcon /> },
        { name: 'Notificaciones',      page: 'admin_notifications', icon: <BellIcon />, badge: adminUnreadCount },
        { name: 'Gestión de Usuarios', page: 'admin_users',       icon: <UsersIcon /> },
        { name: 'Historial y correos', page: 'admin_activity', icon: <ClipboardListIcon /> },
        { name: 'Plazos',              page: 'admin_deadlines',   icon: <CalendarIcon /> },
      ]},
      { label: 'Reportes', items: publicItems },
      { label: 'Legal', items: legalItems },
    ];
    if (currentUser?.role === 'company') return [
      { label: 'Mi Cuenta', items: [
        { name: 'Llenar Cuestionario', page: 'company_form',     icon: <PencilIcon /> },
        { name: 'Mi Perfil',           page: 'company_profile',  icon: <BuildingOfficeIcon /> },
        { name: 'Mis Recompensas',     page: 'company_rewards',  icon: <StarIcon /> },
      ]},
      { label: 'Reportes', items: publicItems },
      { label: 'Legal', items: legalItems },
    ];
    return [
      { label: null, items: publicItems },
      { label: 'Legal', items: legalItems },
    ];
  })();

  const NavButton = ({ item }) => {
    const isActive = currentPage === item.page;
    return (
      <button
        onClick={() => {
          onNav(item.page);
          onClose();
        }}
        className={`group flex items-center w-full px-3 py-2.5 max-[700px]:py-2 rounded-xl transition-all duration-150 gap-3 text-sm ${
          isActive
            ? 'bg-[#004AAD]/35 text-white'
            : 'text-white/45 hover:text-white/80 hover:bg-white/[0.05]'
        }`}
      >
        <span className={`flex-shrink-0 w-[18px] h-[18px] transition-colors ${isActive ? 'text-[#6AABFF]' : 'text-white/30 group-hover:text-white/55'}`}>
          {item.icon}
        </span>
        <span className={`font-medium flex-1 text-left ${isActive ? 'text-white' : ''}`}>{item.name}</span>
        {item.badge > 0 ? (
          <span className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[0.65rem] font-bold ${
            isActive ? 'bg-white/20 text-white' : 'bg-[#004AAD] text-white'
          }`}>
            {item.badge}
          </span>
        ) : (
          isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#4A8FE8] flex-shrink-0" />
        )}
      </button>
    );
  };

  const roleLabel = { admin: 'Administrador', company: 'Empresa', public: 'Público' };

  return (
    <div className={`fixed top-0 left-0 z-50 h-[100dvh] max-h-[100dvh] w-[19rem] max-w-[92vw] bg-[#0B1728] text-white flex flex-col overflow-hidden border-r border-white/[0.06] shadow-none transition-transform duration-200 lg:h-screen lg:max-h-none lg:w-64 lg:max-w-none lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      {/* Brand */}
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-white/[0.07] px-5 pb-4 pt-[calc(1.1rem+env(safe-area-inset-top))] sm:px-6 sm:pb-6 sm:pt-7 max-[700px]:pb-3">
        <div className="min-w-0">
          <SidebarFedexporMark />
        </div>
        <button
          type="button"
          onClick={onClose}
          className="lg:hidden flex h-9 w-9 items-center justify-center rounded-xl bg-white/[0.06] text-white/70"
          aria-label="Cerrar navegación"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Nav */}
      <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 flex flex-col gap-4 sm:py-5 sm:gap-5 max-[700px]:py-3 max-[700px]:gap-3">
        {sections.map((section, si) => (
          <div key={si}>
            {section.label && (
              <p className="px-3 mb-2 text-[0.6rem] font-semibold uppercase text-white/28">
                {section.label}
              </p>
            )}
            <div className="flex flex-col gap-0.5">
              {section.items.map(item => <NavButton key={item.page} item={item} />)}
            </div>
          </div>
        ))}
      </nav>

      {/* User footer */}
      {currentUser && (
        <div className="shrink-0 px-3 pb-[calc(0.85rem+env(safe-area-inset-bottom))] pt-3 border-t border-white/[0.07] lg:pb-5 max-[700px]:pt-2">
          <PoweredByLytiks variant="dark" className="mb-2.5 max-[700px]:mb-2" />
          <div className="flex items-center gap-3 px-3 py-2.5 mb-1 rounded-xl bg-white/[0.05] sm:py-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#004AAD] to-[#3B82F6] flex items-center justify-center text-xs font-bold flex-shrink-0 uppercase shadow">
              {(currentUser.name || roleLabel[currentUser.role] || 'U').charAt(0)}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white/90 truncate leading-tight">{currentUser.name || roleLabel[currentUser.role]}</p>
              <p className="text-[0.65rem] text-white/35 mt-0.5">{roleLabel[currentUser.role] ?? currentUser.role}</p>
            </div>
          </div>
          <button
            onClick={() => {
              onLogout();
              onClose();
            }}
            className="group flex items-center w-full px-3 py-2.5 max-[700px]:py-2 rounded-xl text-white/35 hover:text-white/70 hover:bg-white/[0.05] transition-all duration-150 gap-3 text-sm mt-0.5"
          >
            <span className="w-[18px] h-[18px] flex-shrink-0 transition-colors group-hover:text-white/60">
              <LogoutIcon />
            </span>
            <span className="font-medium">{currentUser.role === 'public' ? 'Volver al acceso' : 'Salir'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
